import React, { useCallback, useMemo, useEffect, useState } from "react";
import moment from "moment";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ReferenceLine,
} from "recharts";
import { flatten, isNil, values, reject } from "ramda";
import { DateRangePicker } from "src/main_app/components/date_range_picker";
import { formatNumber, randomColor } from "src/main_app/utils/common";
import { saveAs } from "file-saver";
import { useTenantConfig } from "src/main_app/actions/tenant";
import * as htmlToImage from "html-to-image";

const tooltipContentStyle = {
  boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
  borderRadius: "4px",
  border: "none",
  textAlign: "center",
};

const tooltipItemStyle = {
  color: "#05d6f",
  fontSize: "12px",
  fontWeight: "500",
};

const ticksFormatter = (value, index) =>
  moment(value * 1000).format("ddd MMM DD");

const renderTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      className="custom-line-tooltip"
      style={{
        padding: "5px",
        backgroundColor: "#fff",
        boxShadow: "0 0 8px 8px rgb(0 0 0 / 7%)",
        borderRadius: "4px",
      }}
    >
      <h5 className="mb-0">{payload[0].payload.displayDate}</h5>
      <br />
      {payload.map((payloadItem, index) => (
        <div
          key={index + ""}
          style={{
            color: payloadItem.stroke,
          }}
        >
          {payloadItem.name}: {formatNumber(payloadItem.value)}{" "}
          {payloadItem.unit}
        </div>
      ))}
    </div>
  );
};

const CustomersPressureUsagePage = ({
  hasRealtimeData,
  dateRangeDataWeekly,
  getDataWeekly,
  dataWeekly,
  meterNames,
  handleDownloadDataPressure,
  ticks,
  customer,
}) => {
  const tenantConfig = useTenantConfig();
  const checkHasRealtimeData = [];
  if (hasRealtimeData) {
    for (const itemDataWeekly of dataWeekly) {
      if (itemDataWeekly["date"] != undefined) {
        checkHasRealtimeData.push(itemDataWeekly);
        break;
      }
    }
  }

  const allSumData = useMemo(() => {
    const dataUsage = [];
    if (!hasRealtimeData) return dataUsage;

    for (const itemDataWeekly of dataWeekly) {
      const { date, displayDate, ...dataSumUsage } = itemDataWeekly;
      dataUsage.push(values(dataSumUsage));
    }
    return reject(isNil, flatten(dataUsage));
  }, [dataWeekly, hasRealtimeData]);

  const dataWeeklyMax = useMemo(
    () => Math.ceil(Math.max(...allSumData) * 10) / 10,
    [allSumData]
  );

  const onApplyWeeklyConsumption = useCallback(
    (event, picker) => {
      getDataWeekly &&
        getDataWeekly(
          picker.startDate.utcOffset(0, true).toDate(),
          picker.endDate.utcOffset(0, true).toDate()
        );
    },
    [getDataWeekly]
  );

  const yLabelSUMs = useMemo(
    () => ({
      value: "m head",
      angle: -90,
      position: "top",
      dy: 170,
      dx: -10,
    }),
    []
  );

  useEffect(() => {
    import("./style.scss");
  }, []);

  //Real-time chart
  const handleRealTimeDownload = () => {
    htmlToImage
      .toPng(document.getElementById("realtime-chart"))
      .then(function (dataUrl) {
        saveAs(dataUrl, "weekly-pressure-chart.png");
      });
  };

  const [aspect, setAspect] = useState(getAspect(window.innerWidth));

  useEffect(() => {
    const handleResize = () => setAspect(getAspect(window.innerWidth));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function getAspect(width) {
    if (width < 480) return 10 / 7; // taller for mobile
    if (width < 768) return 10 / 5; // tablet
    return 10 / 3; // desktop
  }

  return (
    <>
      <div className="container-fluid content">
        <h3 className="sub-title">Weekly Pressure</h3>
        <div className="form-group">
          <label>Date range</label>
          <DateRangePicker
            setting={{
              startDate: dateRangeDataWeekly[0],
              endDate: dateRangeDataWeekly[1],
            }}
            onApply={onApplyWeeklyConsumption}
          >
            <input type="text" className="form-control" />
          </DateRangePicker>
        </div>
        <>
          <div>
            <a
              className="btn btn-primary btn-sm"
              onClick={handleRealTimeDownload}
            >
              Download as image
            </a>
            <a
              className="btn btn-primary btn-sm ml-2"
              onClick={handleDownloadDataPressure}
            >
              Download data
            </a>
          </div>
          <div className="card" id="realtime-chart">
            <div className="card-body">
              <ResponsiveContainer width="100%" aspect={aspect}>
                <LineChart
                  height={180}
                  data={dataWeekly}
                  margin={{
                    top: 20,
                    right: 5,
                    left: -15,
                    bottom: 5,
                  }}
                  barGap={5}
                  barCategoryGap={5}
                >
                  <CartesianGrid stroke="#e9ebf1" />
                  <XAxis
                    dataKey="date"
                    stroke="#e9ebf1"
                    type={"category"}
                    interval={"preserveStartEnd"}
                    ticks={ticks}
                    tickFormatter={ticksFormatter}
                  />
                  <YAxis
                    stroke="#e9ebf1"
                    domain={[0, dataWeeklyMax]}
                    label={yLabelSUMs}
                  />
                  {meterNames.map((meterName, index) => (
                    <Line
                      key={index + ""}
                      type="monotone"
                      dataKey={meterName}
                      name={
                        tenantConfig.isConcatMeterAddress
                          ? meterName +
                            " - " +
                            customer.address +
                            `, ${customer.suburb_name}`
                          : meterName
                      }
                      stroke={index === 0 ? "#09A3B3" : randomColor(index)}
                      dot={false}
                      unit={" (m head)"}
                    />
                  ))}

                  <ReferenceLine
                    y={150}
                    stroke="#f44336"
                    ifOverflow="extendDomain"
                    label={{
                      value: "150",
                      position: "insideTopRight",
                      fill: "#f44336",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />
                  <ReferenceLine
                    y={10}
                    stroke="#4caf50"
                    ifOverflow="extendDomain"
                    label={{
                      value: "10",
                      position: "insideBottomRight",
                      fill: "#4caf50",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />

                  <Tooltip
                    contentStyle={tooltipContentStyle}
                    itemStyle={tooltipItemStyle}
                    content={renderTooltip}
                  />
                  <Legend iconType={"circle"} margin={{ top: 50 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      </div>
    </>
  );
};

export default CustomersPressureUsagePage;
